import React from 'react';
import type { RoomRate } from '@niarab2c/frontend-commons/src/types/hotels';
import { NiaraIcon } from '@niaratech/niara-icons';
import { printPercentage } from '@niaratech/niara-js-commons';
import { ResponsiveTooltip, Tooltip } from '@niaratech/niara-react-components';
import { IconButton } from '@niaratech/niara-react-components/src/controls/AppWrapper/styles';
import { readableColor } from 'polished';
import { AddonCart, AddonCartItem, ModalOTA, ModalOTAProps, TaxIncluded } from '../..';
import { Text, useHotelSearchContext, useTheme } from '../../..';
import Badge from '../../../microComponents/Badge';
import { Meal, RatePlanInclusions, SelectButton } from '../../../microComponents';
import exhibitionChildrensLabel from '../../../util/Occupation/exhibitionChildrensLabel';
import Policy, { PolicyProps } from '../../Policy';
import Price from '../../Price';
import * as S from './styles';
export type CartRoomProps = {
  itemId?: string;
  hotelName: string;
  hotelImage?: string;
  cityName: string;
  stateName: string;
  roomTitle: string;
  guests: number;
  totalValue: number;
  currency: string;
  nights: number;
  taxes: number;
  policy?: PolicyProps;
  children?: number;
  childrenAges?: number[];
  roomRates: [RoomRate];
  roomImage?: string;
  hotelId?: string;
  startDate?: string;
  endDate?: string;
  onRemove?: () => void;
  /**
   * Habilitará input select para alterar a quantidade de itens no carrinho.
   ** `Feature 224215`
   */
  setQuantity?: (value: number) => void;
  quantity?: number;
  maxQuantity?: number;
  badge?: string;
  badgeDescription?: string;
  badgeColor?: string;
  badgeHighlighted?: boolean;
  policyDescription?: Description[];
  /** @deprecated*/
  promotion?: {
    code: string;
    description: string;
    total: number;
    currency: string;
  };
  showComissionAndMarkup?: 'tenant' | 'client' | false | null;
  showOnlyTotal?: boolean;
  disableTax?: boolean;
  isForGroups?: boolean;
  addonCartItems?: AddonCartItem[];
  itemIds?: string[];
};
type Description = {
  name?: string;
  description?: string;
};
const CartRoom: React.FC<CartRoomProps> = props => {
  const {
    roomTitle,
    policy,
    guests,
    currency,
    totalValue,
    nights = 1,
    taxes,
    children,
    childrenAges,
    onRemove,
    badge,
    roomRates,
    showComissionAndMarkup,
    maxQuantity,
    setQuantity,
    quantity,
    showOnlyTotal,
    disableTax,
    policyDescription,
    badgeHighlighted,
    badgeDescription,
    badgeColor,
    isForGroups,
    addonCartItems,
    itemIds
  } = props;
  const theme = useTheme();
  const roomRate = roomRates?.[0];
  const inclusions = roomRate?.ratePlan?.inclusions;
  const {
    showRatePlanName
  } = useHotelSearchContext();
  const discounts = (roomRate?.priceComposition?.previewDiscountBreakdown ?? [])?.concat(roomRate?.priceComposition?.discountBreakdown ?? []);
  const badgeSlotColor = badgeColor ?? (badgeHighlighted ? theme?.colors?.priceColor : theme?.colors?.textColor);
  const badgeSlotColorReadable = badgeColor ? readableColor(badgeColor) : theme?.colors?.white;
  const addon = React.useMemo(() => addonCartItems?.length > 0 && addonCartItems?.filter(item => itemIds?.includes(item?.ownerId)), [addonCartItems, itemIds]);
  const onRemoveitem = () => {
    onRemove();
    if (addon?.length > 0) addon?.forEach(addon => addon?.onRemove?.());
  };
  return <S.ItemStyle>
      <S.ContentItemlStyle>
        <S.HotelCartTitle>
          {quantity > 0 && <span>({quantity}×) </span>}
          {roomTitle}
        </S.HotelCartTitle>

        <IconButton onClick={onRemoveitem} sx={{
        position: 'absolute',
        top: '-7px',
        right: '-7px'
      }}>
          <Tooltip title={"Remover"}>
            <NiaraIcon icon="sysDelete" />
          </Tooltip>
        </IconButton>
      </S.ContentItemlStyle>

      <S.PersonStyle>
        <NiaraIcon icon="sysPersonalInformation" />
        <Text as="p" fontSize={12}>{` ${guests} ${guests > 1 ? "Adultos" : "Adulto"} ${children > 0 && childrenAges?.length > 0 ? `, ${children} ${children > 1 ? "Crianças" : "Criança"} ${exhibitionChildrensLabel({
          childrenAges
        })}` : ''}`}</Text>
      </S.PersonStyle>

      {showRatePlanName && roomRate?.ratePlan?.name && <Text as="p" fontWeight={500} fontSize={12}>
          {roomRate?.ratePlan?.name}
        </Text>}

      <Meal meal={roomRate?.meal} mealDescriptions={roomRate?.descriptions?.meal} />

      <RatePlanInclusions inclusions={inclusions} initialVisibleInclusions={5} addons={roomRate?.addons} />

      {policy && <Policy policyDescription={policyDescription} {...policy} textSize={12} fontWeight="700" showPricePolicy />}
      <S.DescStyle>
        {(badge || discounts?.length > 0) && <S.Badges>
            {badge && <Badge color={badgeSlotColor} colorText={badgeSlotColorReadable} borderRadius="2px" borderRadiusMb="2px" height="20px" fontWeight="600" fontSize="12px">
                {badgeDescription ? <ResponsiveTooltip<ModalOTAProps> modalComponent={ModalOTA} ModalProps={{
            title: badge
          }} renderContent={() => <>{badgeDescription}</>}>
                    {badge}
                  </ResponsiveTooltip> : badge}
              </Badge>}
            {discounts?.map((discount, idx) => <Badge key={idx} color={theme?.colors?.priceColor} colorText={theme?.colors?.white} borderRadius="2px" borderRadiusMb="2px" height="20px" fontWeight="600" fontSize="12px">
                {discount?.description}
              </Badge>)}
            {/* {promotion && (
             <Badge
              color={theme?.colors?.gray3}
              colorText={theme?.colors?.textColor}
              borderRadius="15px"
              borderRadiusMb="15px"
              height="20px"
             >
              {promotion?.description}
             </Badge>
             )} */}
          </S.Badges>}
      </S.DescStyle>

      {addon?.length > 0 && <AddonCart addonCartItems={addon} isCartView={true} horizontalOrientation={true} hideCartTotal={true} />}

      {disableTax ? <></> : <>
          {!!taxes && taxes > 0 && <TaxIncluded value={taxes} text="Impostos" currency={currency} valueSize={11} dataTestId="cartroom-taxes" />}
        </>}

      {roomRate?.priceComposition?.fee && <TaxIncluded value={roomRate?.priceComposition?.fee?.value} text={roomRate?.priceComposition?.fee?.label} currency={roomRate?.priceComposition?.fee?.currency} valueSize={11} />}
      {showComissionAndMarkup == 'tenant' && <>
          {roomRate?.priceComposition?.markup && <div>
              <TaxIncluded value={roomRate?.priceComposition?.markup?.value} text={`Markup (${printPercentage(roomRate?.priceComposition?.markup?.value / totalValue)})`} currency={roomRate?.priceComposition?.markup?.currency} valueSize={11} dataTestId="cartroom-markup" />
            </div>}
          {roomRate?.priceComposition?.commission && <div>
              <TaxIncluded value={roomRate?.priceComposition?.commission?.value} text={`Comissão (${printPercentage(roomRate?.priceComposition?.commission?.value / totalValue)})`} currency={roomRate?.priceComposition?.commission?.currency} valueSize={11} dataTestId="cartroom-commission" />
            </div>}

          {roomRate?.priceComposition?.clientCommission && <div>
              <TaxIncluded value={roomRate?.priceComposition?.clientCommission?.value} text={`Comissão cliente (${printPercentage(roomRate?.priceComposition?.clientCommission?.value / totalValue)})`} currency={roomRate?.priceComposition?.clientCommission?.currency} valueSize={11} dataTestId="cartroom-client-commission" />
            </div>}
        </>}
      {showComissionAndMarkup == 'client' && roomRate?.priceComposition?.clientCommission && <div>
          <TaxIncluded value={roomRate?.priceComposition?.clientCommission?.value} text={`Comissão cliente (${printPercentage(roomRate?.priceComposition?.clientCommission?.value / totalValue)})`} currency={roomRate?.priceComposition?.clientCommission?.currency} valueSize={11} dataTestId="cartroom-client-commission" />
        </div>}

      <>
        {/**Funcionará bem quando for possível alterar número de itens no carrinho a partir do carrinho.#224215 */}
        {setQuantity && isForGroups ? <S.ChangeQuantityRoomsContainer>
            <p className="quantity-rooms">Quantidade de quarto</p>
            <div>
              <SelectButton block maxValue={maxQuantity} value={quantity} onValue={setQuantity} enforceSelectInput={isForGroups} />
            </div>
          </S.ChangeQuantityRoomsContainer> : <></>}
      </>

      {showOnlyTotal ? <S.ValStyle>
          <Price value={totalValue} currency={currency} color={theme.colors?.textColor} currencySize={12} priceCoinSize={13} fractionSize={13} decimalSize={13} fontWeightAll="700" dataTestId="cart-room-per-night-total" />
        </S.ValStyle> : <S.ValStyle>
          <div>
            <Price value={totalValue / nights} currency={currency} color={theme.colors?.gray6} currencySize={12} priceCoinSize={13} fractionSize={13} decimalSize={13} currencyFontWeight={'bold'} priceCoinFontWeight={'bold'} fractionFontWeight={'bold'} dataTestId="cart-room-price-per-night" />
            <span id="nights">{' /noite'}</span>
          </div>
          <div>
            <Price value={totalValue} currency={currency} color={theme.colors?.textColor} currencySize={12} priceCoinSize={14} fractionSize={12} decimalSize={12} currencyFontWeight={'normal'} priceCoinFontWeight={'bold'} fractionFontWeight={'bold'} dataTestId="cart-room-per-night-total" />
          </div>
        </S.ValStyle>}
    </S.ItemStyle>;
};
export default CartRoom;